import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, onSnapshot, doc, deleteDoc, query, where, or } from 'firebase/firestore';
import toast from 'react-hot-toast';
import SkeletonLoader from '../components/SkeletonLoader';
import HomeworkPlayer from '../components/HomeworkModule/HomeworkPlayer';
import DeepReviewBoard from '../components/HomeworkModule/DeepReviewBoard';
import KidsGameEngine from '../HomeworkKidsGames/KidsGameEngine';
import { BOOTCAMP_CLASSES } from '../utils/kidsBootcampData';
import HomeworkSolver from '../components/StudentTabs/HomeworkSolver';

// 🔥 THE FIX: Yahan saare Lichess Themes add kar diye hain!
const TACTICAL_TOPICS = [
    // Basic Mates
    { id: 1, name: "Mate in 1", icon: "🎯", tag: "mateIn1" },
    { id: 2, name: "Mate in 2", icon: "♟️", tag: "mateIn2" },
    { id: 3, name: "Mate in 3+", icon: "🔥", tag: "mateIn3" },
    { id: 4, name: "Back-Rank Mate", icon: "🚪", tag: "backRankMate" },
    { id: 5, name: "Smothered Mate", icon: "🗜️", tag: "smotheredMate" },
    { id: 6, name: "Anastasia's Mate", icon: "🏰", tag: "anastasiaMate" },
    { id: 7, name: "Arabian Mate", icon: "🐪", tag: "arabianMate" },
    
    // Core Tactics
    { id: 8, name: "The Fork", icon: "🔱", tag: "fork" },
    { id: 9, name: "The Pin", icon: "📌", tag: "pin" },
    { id: 10, name: "The Skewer", icon: "🍢", tag: "skewer" },
    { id: 11, name: "Discovered Attack", icon: "👻", tag: "discoveredAttack" },
    { id: 12, name: "Double Check", icon: "‼️", tag: "doubleCheck" },
    
    // Intermediate/Advanced Tactics
    { id: 13, name: "Removing the Defender", icon: "💥", tag: "capturingDefender" },
    { id: 14, name: "Deflection", icon: "🧲", tag: "deflection" },
    { id: 15, name: "Attraction", icon: "🎣", tag: "attraction" },
    { id: 16, name: "Clearance", icon: "🧹", tag: "clearance" },
    { id: 17, name: "Intermezzo (In-between)", icon: "⚡", tag: "intermezzo" },
    { id: 18, name: "Interference", icon: "🚧", tag: "interference" },
    { id: 19, name: "X-Ray Attack", icon: "🔫", tag: "xRayAttack" },
    { id: 20, name: "Zugzwang", icon: "🛑", tag: "zugzwang" },
    
    // Defense & Position
    { id: 21, name: "Capture Free Piece", icon: "🍎", tag: "hangingPiece" },
    { id: 22, name: "Defensive Move", icon: "🛡️", tag: "defensiveMove" },
    { id: 23, name: "Trapped Piece", icon: "🕸️", tag: "trappedPiece" },
    { id: 24, name: "Winning Advantage", icon: "📈", tag: "crushing" },
    { id: 25, name: "Sacrifice", icon: "🔥", tag: "sacrifice" },
    { id: 26, name: "Exposed King", icon: "🎯", tag: "exposedKing" },
    
    // Phases & Specifics
    { id: 27, name: "Endgame Master", icon: "⏳", tag: "endgame" },
    { id: 28, name: "Pawn Endgame", icon: "♟️", tag: "pawnEndgame" },
    { id: 29, name: "Rook Endgame", icon: "♖", tag: "rookEndgame" },
    { id: 30, name: "Opening Traps", icon: "🪤", tag: "opening" },
    { id: 31, name: "Middlegame Clash", icon: "⚔️", tag: "middlegame" },
    { id: 32, name: "Advanced Pawn", icon: "🚀", tag: "advancedPawn" },
    { id: 33, name: "Promotion Tactics", icon: "👑", tag: "promotion" },
    { id: 34, name: "Underpromotion", icon: "🐴", tag: "underPromotion" },
    { id: 35, name: "En Passant", icon: "👻", tag: "enPassant" }
];

const BEGINNER_TOPICS = BOOTCAMP_CLASSES;

export default function HomeworkManager({ user }) {
    const [myProfile, setMyProfile] = useState(null);
    const [allBatches, setAllBatches] = useState([]);
    const [allHomeworks, setAllHomeworks] = useState([]);
    const [allSubmissions, setAllSubmissions] = useState([]);
    const [allUsers, setAllUsers] = useState([]); 
    
    // Sorting & Filtering States
    const [studentFilter, setStudentFilter] = useState('ALL'); 
    const [studentSort, setStudentSort] = useState('DUE_SOON'); 
    const [coachSort, setCoachSort] = useState('NEWEST'); 
    const [erpSort, setErpSort] = useState('COMPLETION_DESC'); 
    
    const [activeTab, setActiveTab] = useState('pending'); 
    const [activePlayer, setActivePlayer] = useState(null); 
    const [reviewSubmission, setReviewSubmission] = useState(null); 
    
    const [assignTarget, setAssignTarget] = useState('BATCH'); 
    const [selectedBatch, setSelectedBatch] = useState('');
    const [selectedStudent, setSelectedStudent] = useState('');
    
    const [curriculumType, setCurriculumType] = useState('BEGINNER');
    const [selectedTopic, setSelectedTopic] = useState(1);
    const [selectedLevel, setSelectedLevel] = useState(1);
    const [dueDate, setDueDate] = useState('');
    const [selectedLedgerStudent, setSelectedLedgerStudent] = useState('');
    const [ledgerSearch, setLedgerSearch] = useState('');

    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();

        const unsubUser = onSnapshot(doc(db, "users", safeEmail), (docSnap) => {
            if (docSnap.exists()) {
                const profile = { id: docSnap.id, ...docSnap.data() };
                setMyProfile(profile);
                const role = profile.role?.toUpperCase() || '';
                if (role.includes('STUDENT')) setActiveTab('pending');
                else setActiveTab('assign');
            }
        });
        return () => unsubUser();
    }, [user]);

    const safeRole = myProfile?.role?.toUpperCase() || '';
    const isStudent = safeRole.includes('STUDENT');
    const isCoachOrOwner = safeRole.includes('COACH') || ['OWNER', 'ADMIN'].includes(safeRole);
    const safeEmail = user?.email?.toLowerCase() || '';

    useEffect(() => {
        if (!safeRole) return; 
        
        let unsubBatches, unsubSubs, unsubUsers;

        // Batches Subscription
        let qBatches;
        if (['OWNER', 'ADMIN'].includes(safeRole)) {
            qBatches = collection(db, "batches");
        } else if (safeRole.includes('COACH')) {
            qBatches = query(collection(db, "batches"), where("coachEmail", "==", safeEmail));
        } else {
            qBatches = query(collection(db, "batches"), where("students", "array-contains", safeEmail));
        }
        unsubBatches = onSnapshot(qBatches, (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); 
            setAllBatches(list);
        });

        // Submissions Subscription
        let qSubs;
        if (['OWNER', 'ADMIN'].includes(safeRole) || safeRole.includes('COACH')) {
            qSubs = collection(db, "homework_submissions");
        } else {
            qSubs = query(collection(db, "homework_submissions"), where("studentEmail", "==", safeEmail));
        }
        unsubSubs = onSnapshot(qSubs, (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); 
            setAllSubmissions(list);
        });

        // Users Subscription
        if (['OWNER', 'ADMIN'].includes(safeRole) || safeRole.includes('COACH')) {
            const qUsers = query(collection(db, "users"), where("role", "==", "STUDENT"));
            unsubUsers = onSnapshot(qUsers, (snap) => {
                const list = []; 
                snap.forEach(d => list.push({ id: d.id, ...d.data() })); 
                setAllUsers(list);
            });
        }

        return () => { 
            if(unsubBatches) unsubBatches(); 
            if(unsubSubs) unsubSubs(); 
            if(unsubUsers) unsubUsers(); 
        };
    }, [safeRole, safeEmail]); 

    // 📖 Homeworks Subscription (dependent on Batches and Role)
    const allBatchIdsStr = JSON.stringify(allBatches.map(b => b.id).sort());
    useEffect(() => {
        if (!safeRole) return;

        let unsubHW;
        let qHW;

        if (['OWNER', 'ADMIN'].includes(safeRole)) {
            qHW = collection(db, "homeworks");
        } else if (safeRole.includes('COACH')) {
            const batchIds = JSON.parse(allBatchIdsStr);
            if (batchIds.length > 0) {
                qHW = query(
                    collection(db, "homeworks"),
                    or(
                        where("assignerEmail", "==", safeEmail),
                        where("batchId", "in", batchIds)
                    )
                );
            } else {
                qHW = query(collection(db, "homeworks"), where("assignerEmail", "==", safeEmail));
            }
        } else { // STUDENT
            const batchIds = JSON.parse(allBatchIdsStr);
            if (batchIds.length > 0) {
                qHW = query(
                    collection(db, "homeworks"),
                    or(
                        where("assignedTo", "array-contains", safeEmail),
                        where("studentEmail", "==", safeEmail),
                        where("batchId", "in", batchIds)
                    )
                );
            } else {
                qHW = query(
                    collection(db, "homeworks"),
                    or(
                        where("assignedTo", "array-contains", safeEmail),
                        where("studentEmail", "==", safeEmail)
                    )
                );
            }
        }

        unsubHW = onSnapshot(qHW, (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); 
            setAllHomeworks(list.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)));
        });

        return () => {
            if (unsubHW) unsubHW();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeRole, safeEmail, allBatchIdsStr]); 

    if (!myProfile) return <SkeletonLoader />;

    const studentsList = allUsers.map(u => ({ ...u, email: (u.email || u.id).toLowerCase() }));

    const getStudentHomeworks = (studentEmail) => {
        const emailLower = studentEmail.toLowerCase();
        const studentBatches = allBatches.filter(b => (b.students || []).some(em => em.toLowerCase() === emailLower));
        const studentBatchIds = studentBatches.map(b => b.id);
        
        const studentHWs = allHomeworks.filter(hw => {
            if (hw.assignedTo && Array.isArray(hw.assignedTo)) {
                return hw.assignedTo.some(email => email.toLowerCase() === emailLower);
            }
            if (hw.assignTarget === 'STUDENT') return hw.studentEmail?.toLowerCase() === emailLower;
            return studentBatchIds.includes(hw.batchId);
        });
        
        const studentSubs = allSubmissions.filter(s => s.studentEmail?.toLowerCase() === emailLower);
        const studentProfile = studentsList.find(u => u.email.toLowerCase() === emailLower) || {};
        const completedMissions = studentProfile.completedMissions || [];
        
        return studentHWs.map(hw => {
            const hasCompletedSub = studentSubs.some(s => {
                if (s.status !== 'completed') return false;
                if (s.homeworkId === hw.id) return true;
                if (hw.levelId && s.levelId === hw.levelId) return true;
                const matchingHw = allHomeworks.find(h => h.id === s.homeworkId);
                return hw.levelId && matchingHw?.levelId === hw.levelId;
            });
            
            const isCompleted = completedMissions.includes(hw.id) || 
                                completedMissions.includes(hw.levelId) || 
                                hasCompletedSub;
                                
            const inProgressSub = studentSubs.find(s => {
                if (s.status !== 'in_progress') return false;
                if (s.homeworkId === hw.id) return true;
                if (hw.levelId && s.levelId === hw.levelId) return true;
                const matchingHw = allHomeworks.find(h => h.id === s.homeworkId);
                return hw.levelId && matchingHw?.levelId === hw.levelId;
            });
            
            const isInProgress = !isCompleted && !!inProgressSub;
            const isUnattempted = !isCompleted && !isInProgress;
            
            const subToReview = studentSubs.find(s => {
                if (s.homeworkId === hw.id) return true;
                if (hw.levelId && s.levelId === hw.levelId) return true;
                const matchingHw = allHomeworks.find(h => h.id === s.homeworkId);
                return hw.levelId && matchingHw?.levelId === hw.levelId;
            });
            
            return {
                ...hw,
                isCompleted,
                isInProgress,
                isUnattempted,
                progressSub: inProgressSub || null,
                subToReview: subToReview || null,
                batchTitle: hw.assignTarget === 'BATCH' ? (studentBatches.find(b => b.id === hw.batchId)?.title || 'Batch') : null
            };
        });
    };

    const checkSelectedStudentStatus = () => {
        if (!selectedStudent) return null;
        const emailLower = selectedStudent.toLowerCase();
        const studentProfile = studentsList.find(s => s.email.toLowerCase() === emailLower) || {};
        const completedMissions = studentProfile.completedMissions || [];
        
        let targetLvlId = '';
        if (curriculumType === 'TACTICS') {
            const topicInfo = TACTICAL_TOPICS.find(t => t.id === parseInt(selectedTopic));
            if (topicInfo) targetLvlId = `TACTICS_${topicInfo.tag}_${selectedLevel}`;
        } else {
            const topicInfo = BEGINNER_TOPICS.find(t => t.id === parseInt(selectedTopic));
            if (topicInfo) targetLvlId = `KIDS_${topicInfo.tag}_${topicInfo.id}`;
        }
        
        if (!targetLvlId) return null;
        
        const studentBatches = allBatches.filter(b => (b.students || []).some(em => em.toLowerCase() === emailLower)).map(b => b.id);
        const alreadyAssignedHW = allHomeworks.find(hw => {
            if (hw.levelId !== targetLvlId) return false;
            if (hw.assignTarget === 'STUDENT') return hw.studentEmail?.toLowerCase() === emailLower;
            return studentBatches.includes(hw.batchId);
        });
        
        const studentSubs = allSubmissions.filter(s => s.studentEmail?.toLowerCase() === emailLower);
        const hasCompletedSub = studentSubs.some(s => s.status === 'completed' && (s.levelId === targetLvlId || (alreadyAssignedHW && s.homeworkId === alreadyAssignedHW.id)));
        const hasInProgressSub = studentSubs.find(s => s.status === 'in_progress' && (s.levelId === targetLvlId || (alreadyAssignedHW && s.homeworkId === alreadyAssignedHW.id)));
        
        const isCompleted = completedMissions.includes(targetLvlId) || (alreadyAssignedHW && completedMissions.includes(alreadyAssignedHW.id)) || hasCompletedSub;
        
        if (isCompleted) {
            return { status: 'COMPLETED', text: '✅ Student has ALREADY COMPLETED this exercise!', color: '#059669', bg: '#ecfdf5', border: '#10b981' };
        }
        if (hasInProgressSub) {
            const currentIdx = hasInProgressSub.currentIndex !== undefined ? hasInProgressSub.currentIndex + 1 : 1;
            const total = hasInProgressSub.totalPuzzles || 15;
            return { status: 'IN_PROGRESS', text: `⏳ Student is currently working on this (Puzzle ${currentIdx}/${total})!`, color: '#d97706', bg: '#fffbeb', border: '#f59e0b' };
        }
        if (alreadyAssignedHW) {
            return { status: 'ASSIGNED', text: '📋 Already assigned to student (Not started yet)!', color: '#7c3aed', bg: '#f5f3ff', border: '#8b5cf6' };
        }
        return { status: 'SAFE', text: '✨ Safe to assign! This topic has not been given or completed yet.', color: '#2563eb', bg: '#eff6ff', border: '#3b82f6' };
    };

    const statusInfo = checkSelectedStudentStatus();

    const coachBatches = ['OWNER', 'ADMIN'].includes(safeRole) ? allBatches : allBatches.filter(b => b.coachEmail?.toLowerCase() === safeEmail);
    const myBatchIds = coachBatches.map(b => b.id);

    const myStudentBatchIds = allBatches.filter(b => (b.students || []).some(email => email.toLowerCase() === safeEmail)).map(b => b.id);
    const myAssignments = allHomeworks.filter(hw => {
        if (hw.assignedTo && Array.isArray(hw.assignedTo)) {
            return hw.assignedTo.some(email => email.toLowerCase() === safeEmail);
        }
        if (hw.assignTarget === 'STUDENT') return hw.studentEmail?.toLowerCase() === safeEmail;
        return myStudentBatchIds.includes(hw.batchId);
    });

    const completedAssignments = myProfile.completedMissions || [];

    const processedAssignments = myAssignments.map(hw => {
        const hasCompletedSub = allSubmissions.some(s => {
            if (s.studentEmail?.toLowerCase() !== safeEmail || s.status !== 'completed') return false;
            if (s.homeworkId === hw.id) return true;
            if (hw.levelId && s.levelId === hw.levelId) return true;
            const matchingHw = allHomeworks.find(h => h.id === s.homeworkId);
            return hw.levelId && matchingHw?.levelId === hw.levelId;
        });

        const isCompleted = completedAssignments.includes(hw.id) || 
                            completedAssignments.includes(hw.levelId) || 
                            hasCompletedSub;

        const hasInProgressSub = allSubmissions.some(s => {
            if (s.studentEmail?.toLowerCase() !== safeEmail || s.status !== 'in_progress') return false;
            if (s.homeworkId === hw.id) return true;
            if (hw.levelId && s.levelId === hw.levelId) return true;
            const matchingHw = allHomeworks.find(h => h.id === s.homeworkId);
            return hw.levelId && matchingHw?.levelId === hw.levelId;
        });

        const isInProgress = !isCompleted && hasInProgressSub;
        const isUnattempted = !isCompleted && !isInProgress;
        return { ...hw, isCompleted, isInProgress, isUnattempted };
    });

    // De-duplicate assignments by levelId or fallback to homework id
    const uniqueAssignments = [];
    const levelIdsSeen = new Set();
    
    const sortedForDeduplication = [...processedAssignments].sort((a, b) => {
        if (a.isCompleted && !b.isCompleted) return -1;
        if (!a.isCompleted && b.isCompleted) return 1;
        if (a.isInProgress && !b.isInProgress) return -1;
        if (!a.isInProgress && b.isInProgress) return 1;
        return new Date(a.dueDate) - new Date(b.dueDate);
    });

    sortedForDeduplication.forEach(hw => {
        const key = hw.levelId || hw.id;
        if (!levelIdsSeen.has(key)) {
            levelIdsSeen.add(key);
            uniqueAssignments.push(hw);
        }
    });

    const countAll = uniqueAssignments.length;
    const countInProgress = uniqueAssignments.filter(a => a.isInProgress).length;
    const countUnattempted = uniqueAssignments.filter(a => a.isUnattempted).length;
    const countCompleted = uniqueAssignments.filter(a => a.isCompleted).length;

    // Apply Filter
    let filteredAssignments = uniqueAssignments;
    if (studentFilter === 'IN_PROGRESS') {
        filteredAssignments = uniqueAssignments.filter(a => a.isInProgress);
    } else if (studentFilter === 'UNATTEMPTED') {
        filteredAssignments = uniqueAssignments.filter(a => a.isUnattempted);
    } else if (studentFilter === 'COMPLETED') {
        filteredAssignments = uniqueAssignments.filter(a => a.isCompleted);
    }

    // Apply Sort
    const sortedAssignments = [...filteredAssignments].sort((a, b) => {
        if (studentSort === 'DUE_SOON') return new Date(a.dueDate) - new Date(b.dueDate);
        if (studentSort === 'DUE_LATE') return new Date(b.dueDate) - new Date(a.dueDate);
        if (studentSort === 'NEWEST') return new Date(b.createdAt) - new Date(a.createdAt);
        if (studentSort === 'TITLE_AZ') return (a.title || '').localeCompare(b.title || '');
        return 0;
    });

    const visibleHomeworksToCoach = ['OWNER', 'ADMIN'].includes(safeRole) 
        ? allHomeworks 
        : allHomeworks.filter(hw => {
            const assignedByEmail = hw.assignedBy?.toLowerCase() || '';
            const assignerEmail = hw.assignerEmail?.toLowerCase() || '';
            if (assignedByEmail === safeEmail || assignerEmail === safeEmail || hw.assignedBy === myProfile.name) return true;
            if (hw.assignedTo && Array.isArray(hw.assignedTo)) {
                const hasMyStudent = hw.assignedTo.some(email => 
                    coachBatches.some(b => (b.students || []).some(em => em.toLowerCase() === email.toLowerCase()))
                );
                if (hasMyStudent) return true;
            }
            if (hw.assignTarget === 'BATCH' && myBatchIds.includes(hw.batchId)) return true;
            if (hw.assignTarget === 'STUDENT') {
                const isMyStudent = coachBatches.some(b => 
                    (b.students || []).some(email => email.toLowerCase() === hw.studentEmail?.toLowerCase())
                );
                if (isMyStudent) return true;
            }
            return false;
        });

    const sortedVisibleHomeworks = [...visibleHomeworksToCoach].sort((a, b) => {
        if (coachSort === 'NEWEST') return new Date(b.createdAt) - new Date(a.createdAt);
        if (coachSort === 'OLDEST') return new Date(a.createdAt) - new Date(b.createdAt);
        if (coachSort === 'DUE_SOON') return new Date(a.dueDate) - new Date(b.dueDate);
        if (coachSort === 'DUE_LATE') return new Date(b.dueDate) - new Date(a.dueDate);
        if (coachSort === 'ACTIVE') {
            const subsA = allSubmissions.filter(s => s.homeworkId === a.id).length;
            const subsB = allSubmissions.filter(s => s.homeworkId === b.id).length;
            return subsB - subsA;
        }
        return 0;
    });

    const handleAssignHomework = async (e) => {
        e.preventDefault();
        
        if (assignTarget === 'BATCH' && !selectedBatch) return toast.error("Please select a target batch!");
        if (assignTarget === 'STUDENT' && !selectedStudent) return toast.error("Please select a student!");
        if (!dueDate) return toast.error("Please select a due date!");

        let smartLevelId = '';
        let finalTitle = '';

        if (curriculumType === 'TACTICS') {
            const topicInfo = TACTICAL_TOPICS.find(t => t.id === parseInt(selectedTopic));
            smartLevelId = `TACTICS_${topicInfo.tag}_${selectedLevel}`;
            finalTitle = `${topicInfo.icon} ${topicInfo.name} - Level ${selectedLevel}`;
        } else {
            const topicInfo = BEGINNER_TOPICS.find(t => t.id === parseInt(selectedTopic));
            smartLevelId = `KIDS_${topicInfo.tag}_${topicInfo.id}`;
            finalTitle = `${topicInfo.icon} ${topicInfo.name}`;
        }

        let targetData = {};
        if (assignTarget === 'BATCH') {
            const batchInfo = allBatches.find(b => b.id === selectedBatch) || {};
            targetData = { assignTarget: 'BATCH', batchId: selectedBatch, batchTitle: batchInfo.title || 'Unknown Batch' };
        } else {
            const studentInfo = studentsList.find(s => s.email === selectedStudent) || {};
            const safeStudentName = studentInfo.name || selectedStudent.split('@')[0];
            targetData = { assignTarget: 'STUDENT', studentEmail: selectedStudent, studentName: safeStudentName };
        }

        const loadingId = toast.loading("Deploying Mission...");
        try {
            await addDoc(collection(db, "homeworks"), {
                title: finalTitle,
                levelId: smartLevelId,
                chapterId: 'LIBRARY', 
                ...targetData, 
                assignedBy: myProfile.name || safeEmail,
                assignerEmail: safeEmail, 
                dueDate: dueDate,
                createdAt: new Date().toISOString()
            });
            toast.success("Homework Assigned Successfully!", { id: loadingId });
            setSelectedBatch(''); setSelectedStudent(''); setDueDate('');
        } catch (error) {
            toast.error("Failed to assign.", { id: loadingId });
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm("Delete this assignment?")) {
            await deleteDoc(doc(db, "homeworks", id));
            toast.success("Deleted!");
        }
    }

    if (activePlayer) {
        if (activePlayer.materials && activePlayer.materials.length > 0) {
            return <HomeworkSolver homework={activePlayer} userProfile={myProfile} goBack={() => setActivePlayer(null)} />;
        }
        if (String(activePlayer.levelId || '').startsWith('KIDS_')) {
            return <KidsGameEngine assignment={activePlayer} myProfile={myProfile} onBack={() => setActivePlayer(null)} />;
        }
        return <HomeworkPlayer assignment={activePlayer} myProfile={myProfile} onBack={() => setActivePlayer(null)} />;
    }

    if (reviewSubmission) return <DeepReviewBoard submission={reviewSubmission} onClose={() => setReviewSubmission(null)} />;

    const currentList = curriculumType === 'TACTICS' ? TACTICAL_TOPICS : BEGINNER_TOPICS;

    return (
        <div style={{ width: '100%', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
            <div style={{width: '100%', maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
            <div className="glass-panel" style={{display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px', padding: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                <div style={{fontSize: '45px', filter: 'drop-shadow(0 4px 6px rgba(163, 177, 198, 0.4))' }}>📝</div>
                <div>
                    <h1 style={{color: 'var(--text-primary)', margin: 0, fontSize: '32px', fontWeight: '800' }}>Homework Tracker</h1>
                    <p style={{color: '#2563eb', margin: '5px 0 0 0', fontWeight: '800', fontSize: '15px' }}>
                        {isStudent ? 'Complete your pending missions!' : 'Assign & Review Student Progress'}
                    </p>
                </div>
            </div>

            <style>{`
                .tab-sub-btn {
                    padding: 8px 18px; border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); font-size: 13px; white-space: nowrap; border: none; background: transparent; color: var(--text-secondary); outline: none; display: inline-flex; align-items: center; gap: 8px; font-family: 'Outfit', sans-serif;
                }
                .tab-sub-btn:hover {
                    background: rgba(163, 177, 198, 0.15); color: var(--text-primary); transform: translateY(-1.5px);
                }
                .tab-sub-btn-active {
                    padding: 8px 18px; border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); font-size: 13px; white-space: nowrap; border: none; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; outline: none; box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.35); display: inline-flex; align-items: center; gap: 8px; font-family: 'Outfit', sans-serif;
                }
            `}</style>
            
            <div style={{ display: 'flex', gap: '6px', marginBottom: '28px', overflowX: 'auto', padding: '6px', background: 'var(--bg-primary)', borderRadius: '18px', border: '1px solid rgba(163, 177, 198, 0.3)', boxShadow: 'var(--panel-shadow)', alignItems: 'center' }}>
                {isStudent ? (
                    <button 
                        onClick={() => setActiveTab('pending')} 
                        className="kid-btn-bubble"
                        style={{
                            padding: '10px 22px', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', fontSize: '13px', whiteSpace: 'nowrap',
                            background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                            color: '#fff',
                            boxShadow: '0 6px 18px rgba(124, 58, 237, 0.35)'
                        }}
                    >
                        📖 My Homework Quests
                    </button>
                ) : (
                    <>
                        <button onClick={() => setActiveTab('assign')} className={activeTab === 'assign' ? "tab-sub-btn-active" : "tab-sub-btn"}>➕ Assign Task</button>
                        <button onClick={() => setActiveTab('track')} className={activeTab === 'track' ? "tab-sub-btn-active" : "tab-sub-btn"} style={activeTab === 'track' ? {background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)'} : {}}>📊 Track Progress</button>
                        <button onClick={() => setActiveTab('studentLedger')} className={activeTab === 'studentLedger' ? "tab-sub-btn-active" : "tab-sub-btn"} style={activeTab === 'studentLedger' ? {background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)'} : {}}>👤 Student Ledger</button>
                        {['OWNER', 'ADMIN'].includes(safeRole) && (
                            <button onClick={() => setActiveTab('erp')} className={activeTab === 'erp' ? "tab-sub-btn-active" : "tab-sub-btn"} style={activeTab === 'erp' ? {background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)', boxShadow: '0 6px 20px rgba(245, 158, 11, 0.4)'} : {}}>📈 Academy ERP Monitor</button>
                        )}
                    </>
                )}
            </div>

            {isStudent && activeTab === 'pending' && (
                <div>
                    {myAssignments.length === 0 ? (
                        <div className="glass-panel kid-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: '800', border: '3px solid rgba(255,255,255,0.95)', background: 'var(--bg-primary)' }}>
                            Woohoo! No pending missions in your quest journal! Go play in the Arena! 🏆🎉
                        </div>
                    ) : (
                        <div>
                            {/* Sorting & Filtering Bar */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '25px' }}>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '6px', borderRadius: '15px' }}>
                                    <button onClick={() => setStudentFilter('ALL')} style={{ padding: '8px 16px', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', background: studentFilter === 'ALL' ? 'var(--bg-primary)' : 'transparent', boxShadow: studentFilter === 'ALL' ? 'var(--neu-shadow)' : 'none', color: studentFilter === 'ALL' ? '#2563eb' : 'var(--text-secondary)', fontSize: '13px', transition: '0.2s' }}>
                                        All ({countAll})
                                    </button>
                                    <button onClick={() => setStudentFilter('IN_PROGRESS')} style={{ padding: '8px 16px', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', background: studentFilter === 'IN_PROGRESS' ? 'var(--bg-primary)' : 'transparent', boxShadow: studentFilter === 'IN_PROGRESS' ? 'var(--neu-shadow)' : 'none', color: studentFilter === 'IN_PROGRESS' ? '#d97706' : 'var(--text-secondary)', fontSize: '13px', transition: '0.2s' }}>
                                        ⏳ Resume ({countInProgress})
                                    </button>
                                    <button onClick={() => setStudentFilter('UNATTEMPTED')} style={{ padding: '8px 16px', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', background: studentFilter === 'UNATTEMPTED' ? 'var(--bg-primary)' : 'transparent', boxShadow: studentFilter === 'UNATTEMPTED' ? 'var(--neu-shadow)' : 'none', color: studentFilter === 'UNATTEMPTED' ? '#7c3aed' : 'var(--text-secondary)', fontSize: '13px', transition: '0.2s' }}>
                                        ⚔️ Unattempted ({countUnattempted})
                                    </button>
                                    <button onClick={() => setStudentFilter('COMPLETED')} style={{ padding: '8px 16px', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', background: studentFilter === 'COMPLETED' ? 'var(--bg-primary)' : 'transparent', boxShadow: studentFilter === 'COMPLETED' ? 'var(--neu-shadow)' : 'none', color: studentFilter === 'COMPLETED' ? '#059669' : 'var(--text-secondary)', fontSize: '13px', transition: '0.2s' }}>
                                        ✅ Completed ({countCompleted})
                                    </button>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-secondary)' }}>Sort By:</span>
                                    <select value={studentSort} onChange={e => setStudentSort(e.target.value)} style={{ padding: '10px 14px', borderRadius: '10px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', boxShadow: 'var(--neu-shadow)', outline: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                                        <option value="DUE_SOON">🗓️ Due Date: Soonest</option>
                                        <option value="DUE_LATE">🗓️ Due Date: Latest</option>
                                        <option value="NEWEST">🔔 Assigned: Newest</option>
                                        <option value="TITLE_AZ">🔤 Title: A-Z</option>
                                    </select>
                                </div>
                            </div>

                            {sortedAssignments.length === 0 ? (
                                <div className="glass-panel kid-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: '800', border: '3px solid rgba(255,255,255,0.95)', background: 'var(--bg-primary)' }}>
                                    No quests match your status filter. Try checking another tab! 🔍
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {sortedAssignments.map(hw => {
                                        const isCompleted = hw.isCompleted;
                                return (
                                    <div 
                                        key={hw.id} 
                                        className="glass-panel kid-card"
                                        style={{
                                            background: 'var(--bg-primary)', 
                                            boxShadow: isCompleted ? 'var(--neu-shadow-inset)' : 'var(--panel-shadow)', 
                                            padding: '22px 30px', 
                                            borderRadius: '28px', 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            border: isCompleted ? '3px solid #10b981' : '3px solid rgba(255,255,255,0.95)',
                                            flexWrap: 'wrap',
                                            gap: '15px'
                                        }}
                                    >
                                        <div>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                                                {isCompleted ? (
                                                    <span style={{ 
                                                        background: 'linear-gradient(135deg, #10b981, #059669)', 
                                                        color: '#fff', 
                                                        fontSize: '10px', 
                                                        fontWeight: '600', 
                                                        padding: '4px 10px', 
                                                        borderRadius: '12px',
                                                        border: '1.5px solid #fff',
                                                        boxShadow: '0 2px 6px rgba(16,185,129,0.2)'
                                                    }}>
                                                        ✅ COMPLETED
                                                    </span>
                                                ) : (
                                                    <span style={{ 
                                                        background: 'linear-gradient(135deg, #ef4444, #dc2626)', 
                                                        color: '#fff', 
                                                        fontSize: '10px', 
                                                        fontWeight: '600', 
                                                        padding: '4px 10px', 
                                                        borderRadius: '12px',
                                                        border: '1.5px solid #fff',
                                                        boxShadow: '0 2px 6px rgba(239,68,68,0.2)'
                                                    }}>
                                                        ⏳ DUE: {new Date(hw.dueDate).toDateString()}
                                                    </span>
                                                )}
                                                
                                                {hw.assignTarget === 'STUDENT' && (
                                                    <span style={{ 
                                                        background: 'var(--bg-primary)', 
                                                        boxShadow: 'var(--neu-shadow-inset)', 
                                                        color: '#f59e0b', 
                                                        fontSize: '10px', 
                                                        fontWeight: '600', 
                                                        padding: '4px 10px', 
                                                        borderRadius: '12px',
                                                        border: '1px solid rgba(255,255,255,0.6)'
                                                    }}>
                                                        ⭐ SOLO QUEST
                                                    </span>
                                                )}
                                            </div>
                                            <h3 style={{ color: 'var(--text-primary)', margin: '0 0 5px 0', fontSize: '20px', fontWeight: '600' }}>
                                                {hw.title}
                                            </h3>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '500' }}>
                                                🧙‍♂️ Dispatched by: Coach {hw.assignedBy}
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => setActivePlayer({ 
                                                ...hw,
                                                assignmentDocId: hw.id, 
                                                isReplay: isCompleted
                                            })} 
                                            className="kid-btn-bubble animate-float-cute"
                                            style={{
                                                color: '#fff', 
                                                padding: '12px 30px', 
                                                borderRadius: '50px', 
                                                fontWeight: '600', 
                                                cursor: 'pointer', 
                                                border: '3px solid #fff', 
                                                background: isCompleted 
                                                    ? 'linear-gradient(135deg, #10b981, #059669)' 
                                                     : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                                                fontSize: '15px',
                                                boxShadow: isCompleted 
                                                    ? '0 4px 12px rgba(16,185,129,0.3)' 
                                                    : '0 4px 12px rgba(124,58,237,0.3)',
                                                outline: 'none'
                                            }}
                                        >
                                            {isCompleted ? '🔄 Play Again' : '⚔️ Begin Battle'}
                                        </button>
                                    </div>
                                )
                            })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isCoachOrOwner && activeTab === 'assign' && (
                <div className="glass-panel" style={{padding: '30px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                    
                    <div style={{display: 'flex', gap: '10px', marginBottom: '30px', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '6px', borderRadius: '12px' }}>
                        <button type="button" onClick={() => {setCurriculumType('BEGINNER'); setSelectedTopic(1); setSelectedLevel(1);}} style={{flex: 1, padding: '12px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', background: curriculumType === 'BEGINNER' ? 'var(--bg-primary)' : 'transparent', boxShadow: curriculumType === 'BEGINNER' ? 'var(--neu-shadow)' : 'none', color: curriculumType === 'BEGINNER' ? '#f59e0b' : 'var(--text-secondary)', fontSize: '15px', transition: '0.2s' }}>🐣 Beginners Bootcamp</button>
                        <button type="button" onClick={() => {setCurriculumType('TACTICS'); setSelectedTopic(1); setSelectedLevel(1);}} style={{flex: 1, padding: '12px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', background: curriculumType === 'TACTICS' ? 'var(--bg-primary)' : 'transparent', boxShadow: curriculumType === 'TACTICS' ? 'var(--neu-shadow)' : 'none', color: curriculumType === 'TACTICS' ? '#2563eb' : 'var(--text-secondary)', fontSize: '15px', transition: '0.2s' }}>⚔️ Advanced Tactics</button>
                    </div>

                    <div style={{display: 'flex', gap: '10px', marginBottom: '25px', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '6px', borderRadius: '12px', width: 'fit-content' }}>
                        <button type="button" onClick={() => setAssignTarget('BATCH')} style={{padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', background: assignTarget === 'BATCH' ? 'var(--bg-primary)' : 'transparent', boxShadow: assignTarget === 'BATCH' ? 'var(--neu-shadow)' : 'none', color: assignTarget === 'BATCH' ? '#2563eb' : 'var(--text-secondary)', transition: '0.2s' }}>🏫 Assign to Batch</button>
                        <button type="button" onClick={() => setAssignTarget('STUDENT')} style={{padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', background: assignTarget === 'STUDENT' ? 'var(--bg-primary)' : 'transparent', boxShadow: assignTarget === 'STUDENT' ? 'var(--neu-shadow)' : 'none', color: assignTarget === 'STUDENT' ? '#f59e0b' : 'var(--text-secondary)', transition: '0.2s' }}>👤 Assign to Student</button>
                    </div>

                    <form onSubmit={handleAssignHomework} style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        {assignTarget === 'BATCH' ? (
                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '8px' }}>Select Batch</label>
                                <select value={selectedBatch} onChange={e => setSelectedBatch(e.target.value)} required style={{width: '100%', padding: '14px', borderRadius: '12px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', boxShadow: 'var(--neu-shadow-inset)', outline: 'none', fontWeight: '600' }}>
                                    <option value="">-- Choose Target Batch --</option>
                                    {coachBatches.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                                </select>
                            </div>
                        ) : (
                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '8px' }}>Select Individual Student</label>
                                <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)} required style={{width: '100%', padding: '14px', borderRadius: '12px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', boxShadow: 'var(--neu-shadow-inset)', outline: 'none', fontWeight: '600' }}>
                                    <option value="">-- Choose Target Student --</option>
                                    {studentsList.map(s => (
                                        <option key={s.email} value={s.email}>
                                            {s.name || s.username || s.email.split('@')[0]} (@{s.username || s.email.split('@')[0]})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '8px' }}>Due Date</label>
                            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '12px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', boxShadow: 'var(--neu-shadow-inset)', boxSizing: 'border-box', outline: 'none', fontWeight: '600' }} />
                        </div>

                        <div style={{gridColumn: '1 / -1', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow)', padding: '20px', borderRadius: '16px' }}>
                            <label style={{color: curriculumType === 'BEGINNER' ? '#f59e0b' : '#2563eb', fontSize: '16px', fontWeight: '800', display: 'block', marginBottom: '15px' }}>
                                {curriculumType === 'BEGINNER' ? '🐣 Select Game from Bootcamp' : '📚 Select Topic from Tactics'}
                            </label>
                            <div style={{display: 'flex', gap: '15px' }}>
                                <div style={{flex: 1 }}>
                                    <label style={{color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '5px', display: 'block', fontWeight: '700' }}>Topic</label>
                                    <select value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)} className="neu-input" style={{fontWeight: '600' }}>
                                        {currentList.map(t => <option key={t.id} value={t.id} style={{background: 'var(--bg-primary)', color: 'var(--text-primary)'}}>{t.icon} {t.name}</option>)}
                                    </select>
                                </div>
                                {curriculumType === 'TACTICS' ? (
                                    <div style={{flex: 1 }}>
                                        <label style={{color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '5px', display: 'block', fontWeight: '700' }}>Difficulty Stage (1-12)</label>
                                        <select value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} className="neu-input" style={{fontWeight: '600' }}>
                                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(l => <option key={l} value={l} style={{background: 'var(--bg-primary)', color: 'var(--text-primary)'}}>Level/Stage {l}</option>)}
                                        </select>
                                    </div>
                                ) : (
                                    <div style={{flex: 1, display: 'flex', alignItems: 'center', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '12px 18px', borderRadius: '12px', color: '#f59e0b', fontSize: '13px', fontWeight: '600' }}>
                                        <span>💡 Each Bootcamp class contains 15 progressive interactive chess puzzles designed for kids (5-8 yrs).</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {statusInfo && (
                            <div style={{
                                gridColumn: '1 / -1',
                                background: statusInfo.bg,
                                color: statusInfo.color,
                                border: `2px solid ${statusInfo.border}`,
                                padding: '15px 20px',
                                borderRadius: '12px',
                                fontWeight: '750',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                boxShadow: 'var(--neu-shadow-inset)'
                            }}>
                                <span>{statusInfo.text}</span>
                            </div>
                        )}

                        <button type="submit" className="neu-btn-primary" style={{gridColumn: '1 / -1', background: curriculumType === 'BEGINNER' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                            🚀 Deploy Mission
                        </button>
                    </form>
                </div>
            )}

            {isCoachOrOwner && activeTab === 'track' && (
                <div style={{display: 'grid', gap: '20px' }}>
                    {/* Sort Selector Bar */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-secondary)' }}>Sort Missions By:</span>
                        <select value={coachSort} onChange={e => setCoachSort(e.target.value)} style={{ padding: '10px 14px', borderRadius: '10px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', boxShadow: 'var(--neu-shadow)', outline: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                            <option value="NEWEST">🔔 Created: Newest</option>
                            <option value="OLDEST">🔔 Created: Oldest</option>
                            <option value="DUE_SOON">🗓️ Due Date: Soonest</option>
                            <option value="DUE_LATE">🗓️ Due Date: Latest</option>
                            <option value="ACTIVE">⚡ Most Submissions/Active</option>
                        </select>
                    </div>

                    {sortedVisibleHomeworks.length === 0 ? (
                        <div style={{color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: 'var(--bg-primary)', borderRadius: '16px', boxShadow: 'var(--neu-shadow-inset)', fontWeight: '600' }}>No homework assigned yet.</div>
                    ) : (
                        sortedVisibleHomeworks.map(hw => {
                            const relatedSubs = allSubmissions.filter(s => {
                                if (s.homeworkId === hw.id) return true;
                                if (hw.levelId && s.levelId === hw.levelId) return true;
                                const matchingHw = allHomeworks.find(h => h.id === s.homeworkId);
                                return hw.levelId && matchingHw?.levelId === hw.levelId;
                            });

                            const submissionsForThisHw = [];
                            const studentEmailsSeen = new Set();
                            
                            const sortedRelatedSubs = [...relatedSubs].sort((a, b) => {
                                if (a.status === 'completed' && b.status !== 'completed') return -1;
                                if (a.status !== 'completed' && b.status === 'completed') return 1;
                                return new Date(b.submittedAt) - new Date(a.submittedAt);
                            });

                            sortedRelatedSubs.forEach(sub => {
                                const email = sub.studentEmail.toLowerCase();
                                if (!studentEmailsSeen.has(email)) {
                                    studentEmailsSeen.add(email);
                                    submissionsForThisHw.push(sub);
                                }
                            });

                            const isIndiv = hw.assignTarget === 'STUDENT';
                            
                            return (
                            <div key={hw.id} className="glass-panel" style={{padding: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{color: isIndiv ? '#f59e0b' : '#10b981', fontSize: '12px', fontWeight: '800', marginBottom: '5px', textTransform: 'uppercase' }}>
                                            {isIndiv ? `👤 ${hw.studentName}` : `🏫 ${hw.batchTitle}`}
                                        </div>
                                        <h3 style={{color: 'var(--text-primary)', margin: '0 0 5px 0', fontSize: '18px', fontWeight: '800' }}>{hw.title}</h3>
                                        <div style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Due: {new Date(hw.dueDate).toDateString()}</div>
                                    </div>
                                    <button onClick={() => handleDelete(hw.id)} style={{background: 'var(--bg-primary)', color: '#ef4444', border: 'none', padding: '10px 14px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: 'var(--neu-shadow)' }} onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'} onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}>🗑️</button>
                                </div>
                                
                                <div style={{marginTop: '20px', paddingTop: '15px', borderTop: '1px solid rgba(163, 177, 198, 0.2)' }}>
                                    <div style={{color: 'var(--text-primary)', fontWeight: '800', fontSize: '14px', marginBottom: '10px' }}>Student Progress:</div>
                                    {submissionsForThisHw.length === 0 ? (
                                        <div style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Waiting for student to start...</div>
                                    ) : (
                                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {/* ⏳ In Progress Students */}
                                            {submissionsForThisHw.filter(s => s.status === 'in_progress').length > 0 && (
                                                <div>
                                                    <div style={{fontSize: '11px', fontWeight: 'bold', color: '#d97706', marginBottom: '6px', letterSpacing: '0.5px' }}>⏳ IN PROGRESS:</div>
                                                    <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                        {submissionsForThisHw.filter(s => s.status === 'in_progress').map(sub => {
                                                            const total = sub.totalPuzzles || (sub.logs ? sub.logs.length : 20);
                                                            const current = sub.currentIndex !== undefined ? sub.currentIndex + 1 : 1;
                                                            return (
                                                                <button 
                                                                    key={sub.id} 
                                                                    onClick={() => setReviewSubmission(sub)}
                                                                    className="neu-btn"
                                                                    style={{padding: '8px 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--bg-primary)', color: '#d97706', border: '1.5px solid #f59e0b', boxShadow: 'var(--neu-shadow)' }}
                                                                    onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'}
                                                                    onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}
                                                                >
                                                                    ⏳ {sub.studentName || sub.studentEmail.split('@')[0]} (Puzzle {current}/{total})
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* ✅ Completed Students */}
                                            {submissionsForThisHw.filter(s => s.status !== 'in_progress').length > 0 && (
                                                <div>
                                                    <div style={{fontSize: '11px', fontWeight: 'bold', color: '#059669', marginBottom: '6px', letterSpacing: '0.5px' }}>✅ COMPLETED:</div>
                                                    <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                        {submissionsForThisHw.filter(s => s.status !== 'in_progress').map(sub => (
                                                            <button 
                                                                key={sub.id} 
                                                                onClick={() => setReviewSubmission(sub)}
                                                                className="neu-btn"
                                                                style={{padding: '8px 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--bg-primary)', color: '#059669', border: '1.5px solid #10b981', boxShadow: 'var(--neu-shadow)' }}
                                                                onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'}
                                                                onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}
                                                            >
                                                                🏆 {sub.studentName || sub.studentEmail.split('@')[0]} (100% Review)
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )})
                    )}
                </div>
            )}

            {isCoachOrOwner && activeTab === 'studentLedger' && (() => {
                const filteredStudents = studentsList.filter(s => {
                    const name = (s.name || '').toLowerCase();
                    const email = (s.email || '').toLowerCase();
                    const username = (s.username || '').toLowerCase();
                    const q = ledgerSearch.toLowerCase();
                    return name.includes(q) || email.includes(q) || username.includes(q);
                });

                if (selectedLedgerStudent) {
                    const student = studentsList.find(s => s.email.toLowerCase() === selectedLedgerStudent.toLowerCase());
                    if (!student) {
                        setSelectedLedgerStudent('');
                        return null;
                    }
                    const emailLower = student.email.toLowerCase();
                    const studentBatches = allBatches.filter(b => (b.students || []).some(em => em.toLowerCase() === emailLower));
                    const hwLog = getStudentHomeworks(emailLower);
                    
                    // Stats
                    const totalHW = hwLog.length;
                    const completedHW = hwLog.filter(h => h.isCompleted).length;
                    const inProgressHW = hwLog.filter(h => h.isInProgress).length;
                    const unattemptedHW = hwLog.filter(h => h.isUnattempted).length;
                    const completionRate = totalHW > 0 ? Math.round((completedHW / totalHW) * 100) : 0;
                    
                    const hwLevelIds = new Set(hwLog.map(h => h.levelId).filter(Boolean));
                    const completedMissions = student.completedMissions || [];
                    const independentCompleted = completedMissions.filter(lvlId => !hwLevelIds.has(lvlId));

                    let rateColor = '#ef4444';
                    if (completionRate > 70) rateColor = '#059669';
                    else if (completionRate > 40) rateColor = '#f59e0b';

                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                            {/* Back Button & Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <button 
                                    onClick={() => setSelectedLedgerStudent('')} 
                                    className="neu-btn"
                                    style={{ padding: '10px 20px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'var(--bg-primary)', color: '#2563eb', boxShadow: 'var(--neu-shadow)' }}
                                    onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'}
                                    onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}
                                >
                                    ⬅️ Back to Student List
                                </button>
                                <span style={{ fontSize: '13px', fontWeight: '850', color: 'var(--text-secondary)' }}>
                                    STUDENT LEDGER / DETAIL VIEW
                                </span>
                            </div>

                            {/* Student Profile Card */}
                            <div className="glass-panel" style={{ padding: '25px', display: 'flex', gap: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)', flexWrap: 'wrap' }}>
                                <div style={{ 
                                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', 
                                    width: '65px', 
                                    height: '65px', 
                                    borderRadius: '50%', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    color: 'white', 
                                    fontSize: '28px',
                                    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.35)',
                                    flexShrink: 0
                                }}>
                                    👤
                                </div>
                                <div style={{ flex: 1, minWidth: '200px' }}>
                                    <h2 style={{ margin: '0 0 5px 0', color: 'var(--text-primary)', fontSize: '24px', fontWeight: '800' }}>{student.name || student.email.split('@')[0]}</h2>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                                        ✉️ {student.email} | @{student.username || student.email.split('@')[0]}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {studentBatches.length === 0 ? (
                                            <span style={{ fontSize: '11px', fontWeight: '800', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', color: 'var(--text-secondary)', padding: '4px 10px', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.3)' }}>No Active Batch</span>
                                        ) : (
                                            studentBatches.map(b => (
                                                <span key={b.id} style={{ fontSize: '11px', fontWeight: '800', background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
                                                    🏫 {b.title}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div style={{ textAlign: 'center', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '12px 18px', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.2)' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', marginBottom: '5px' }}>COMPLETION RATE</div>
                                        <div style={{ fontSize: '24px', fontWeight: '900', color: rateColor }}>{completionRate}%</div>
                                        <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>{completedHW} of {totalHW} missions</div>
                                    </div>
                                    <div style={{ textAlign: 'center', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '12px 18px', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.2)' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', marginBottom: '5px' }}>IN PROGRESS</div>
                                        <div style={{ fontSize: '24px', fontWeight: '900', color: '#d97706' }}>{inProgressHW}</div>
                                        <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>active tasks</div>
                                    </div>
                                    <div style={{ textAlign: 'center', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '12px 18px', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.2)' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', marginBottom: '5px' }}>UNATTEMPTED</div>
                                        <div style={{ fontSize: '24px', fontWeight: '900', color: '#7c3aed' }}>{unattemptedHW}</div>
                                        <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>pending tasks</div>
                                    </div>
                                </div>
                            </div>

                            {/* Homework Log Timeline */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '25px' }}>
                                <div className="glass-panel" style={{ padding: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                    <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>
                                        📋 Assigned Homework Log & Status
                                    </h3>
                                    {hwLog.length === 0 ? (
                                        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '30px', fontWeight: '600' }}>No homework assigned to this student yet.</div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            {hwLog.map(hw => {
                                                let badgeColor = '#7c3aed';
                                                let badgeBg = '#f5f3ff';
                                                let badgeBorder = '#8b5cf6';
                                                let badgeText = '📋 UNATTEMPTED';
                                                
                                                if (hw.isCompleted) {
                                                    badgeColor = '#059669';
                                                    badgeBg = '#ecfdf5';
                                                    badgeBorder = '#10b981';
                                                    badgeText = '✅ COMPLETED';
                                                } else if (hw.isInProgress) {
                                                    badgeColor = '#d97706';
                                                    badgeBg = '#fffbeb';
                                                    badgeBorder = '#f59e0b';
                                                    const cur = hw.progressSub?.currentIndex !== undefined ? hw.progressSub.currentIndex + 1 : 1;
                                                    const tot = hw.progressSub?.totalPuzzles || 15;
                                                    badgeText = `⏳ IN PROGRESS (Puzzle ${cur}/${tot})`;
                                                }

                                                return (
                                                    <div key={hw.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderRadius: '16px', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', flexWrap: 'wrap', gap: '15px' }}>
                                                        <div>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                                                                <span style={{ fontSize: '10px', fontWeight: '850', color: badgeColor, background: badgeBg, border: `1px solid ${badgeBorder}`, padding: '3px 8px', borderRadius: '8px' }}>
                                                                    {badgeText}
                                                                </span>
                                                                {hw.batchTitle && (
                                                                    <span style={{ fontSize: '10px', fontWeight: '800', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '3px 8px', borderRadius: '8px' }}>
                                                                        🏫 Batch: {hw.batchTitle}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>{hw.title}</h4>
                                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                                                📅 Assigned: {new Date(hw.createdAt).toLocaleDateString()} | Due: {new Date(hw.dueDate).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            {hw.subToReview ? (
                                                                <button 
                                                                    onClick={() => setReviewSubmission(hw.subToReview)}
                                                                    className="neu-btn"
                                                                    style={{ padding: '8px 16px', fontSize: '13px', background: 'var(--bg-primary)', color: '#8b5cf6', border: 'none', boxShadow: 'var(--neu-shadow)', fontWeight: 'bold' }}
                                                                    onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'}
                                                                    onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}
                                                                >
                                                                    🔍 Review Progress
                                                                </button>
                                                            ) : (
                                                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.6, fontWeight: '600' }}>Not started yet</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="glass-panel" style={{ padding: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                    <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>
                                        🧩 Other Completed Levels (Independent Progress)
                                    </h3>
                                    {independentCompleted.length === 0 ? (
                                        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '30px', fontWeight: '600' }}>No independent practice completions logged.</div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                            {independentCompleted.map(lvlId => {
                                                const safeLvlId = String(lvlId || '');
                                                let dispTitle = safeLvlId;
                                                let isTactic = safeLvlId.startsWith('TACTICS_');
                                                if (isTactic) {
                                                    const parts = safeLvlId.split('_');
                                                    const tag = parts[1];
                                                    const lvl = parts[2];
                                                    const topicInfo = TACTICAL_TOPICS.find(t => t.tag === tag);
                                                    dispTitle = topicInfo ? `${topicInfo.icon} ${topicInfo.name} - Level ${lvl}` : `Tactics: ${tag} Lvl ${lvl}`;
                                                } else if (safeLvlId.startsWith('KIDS_')) {
                                                    const parts = safeLvlId.split('_');
                                                    const tag = parts[1];
                                                    const topicInfo = BEGINNER_TOPICS.find(t => t.tag === tag);
                                                    dispTitle = topicInfo ? `${topicInfo.icon} ${topicInfo.name}` : `Bootcamp: ${tag}`;
                                                }
                                                return (
                                                    <div key={lvlId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '12px', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(163, 177, 198, 0.2)' }}>
                                                        <span style={{ fontSize: '16px' }}>🏆</span>
                                                        <span style={{ fontSize: '13px', fontWeight: '750', color: 'var(--text-primary)' }}>{dispTitle}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Render Student Selection Dashboard
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        {/* Search & Header Bar */}
                        <div className="glass-panel" style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>
                                👤 Select Student to Audit Ledger
                            </h3>
                            <input 
                                type="text"
                                placeholder="🔍 Search student by name, email, username..."
                                value={ledgerSearch}
                                onChange={e => setLedgerSearch(e.target.value)}
                                className="neu-input"
                                style={{ maxWidth: '350px', padding: '12px 18px', fontSize: '14px' }}
                            />
                        </div>

                        {filteredStudents.length === 0 ? (
                            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: 'var(--bg-primary)', borderRadius: '16px', boxShadow: 'var(--neu-shadow-inset)', fontWeight: '600' }}>
                                No students match your search criteria.
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                                {filteredStudents.map(student => {
                                    const emailLower = student.email.toLowerCase();
                                    const studentBatches = allBatches.filter(b => (b.students || []).some(em => em.toLowerCase() === emailLower));
                                    const hwLog = getStudentHomeworks(emailLower);
                                    
                                    const totalHW = hwLog.length;
                                    const completedHW = hwLog.filter(h => h.isCompleted).length;
                                    const inProgressHW = hwLog.filter(h => h.isInProgress).length;
                                    const completionRate = totalHW > 0 ? Math.round((completedHW / totalHW) * 100) : 0;
                                    
                                    let progressColor = '#ef4444';
                                    if (completionRate > 70) progressColor = '#059669';
                                    else if (completionRate > 40) progressColor = '#f59e0b';

                                    return (
                                        <div 
                                            key={student.id} 
                                            className="glass-panel" 
                                            onClick={() => setSelectedLedgerStudent(emailLower)}
                                            style={{ 
                                                padding: '20px', 
                                                border: 'none', 
                                                background: 'var(--bg-primary)', 
                                                boxShadow: 'var(--panel-shadow)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease-in-out'
                                            }}
                                            onMouseOver={e => { 
                                                e.currentTarget.style.boxShadow = 'var(--panel-shadow-hover)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }} 
                                            onMouseOut={e => { 
                                                e.currentTarget.style.boxShadow = 'var(--panel-shadow)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                                <div style={{ 
                                                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', 
                                                    width: '40px', 
                                                    height: '40px', 
                                                    borderRadius: '50%', 
                                                    display: 'flex', 
                                                    justifyContent: 'center', 
                                                    alignItems: 'center', 
                                                    color: 'white', 
                                                    fontSize: '18px',
                                                    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)'
                                                }}>
                                                    👤
                                                </div>
                                                <div>
                                                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>
                                                        {student.name || student.email.split('@')[0]}
                                                    </h4>
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                                        @{student.username || student.email.split('@')[0]}
                                                    </span>
                                                </div>
                                            </div>

                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '12px' }}>
                                                🏫 Batches: {studentBatches.map(b => b.title).join(', ') || 'None'}
                                            </div>

                                            {/* Progress Summary */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '800', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                                                <span>Homework Completion</span>
                                                <span style={{ color: progressColor }}>{completionRate}% ({completedHW}/{totalHW})</span>
                                            </div>
                                            
                                            <div style={{ width: '100%', height: '8px', background: 'var(--bg-primary)', borderRadius: '4px', overflow: 'hidden', boxShadow: 'var(--neu-shadow-inset)', marginBottom: '12px' }}>
                                                <div style={{ width: `${completionRate}%`, height: '100%', backgroundColor: progressColor }} />
                                            </div>

                                            {inProgressHW > 0 && (
                                                <div style={{ fontSize: '12px', color: '#d97706', fontWeight: '750', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    ⏳ {inProgressHW} active homework session(s)
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })()}


            {['OWNER', 'ADMIN'].includes(safeRole) && activeTab === 'erp' && (() => {
                // Calculate metrics based on unique level assignments per student
                let totalTargetedMissions = 0;
                let totalSubs = 0;

                studentsList.forEach(student => {
                    const studentBatches = allBatches.filter(b => (b.students || []).includes(student.email)).map(b => b.id);
                    const assignedHWs = allHomeworks.filter(hw => {
                        if (hw.assignedTo && Array.isArray(hw.assignedTo)) {
                            return hw.assignedTo.some(email => email.toLowerCase() === student.email.toLowerCase());
                        }
                        if (hw.assignTarget === 'STUDENT') return hw.studentEmail === student.email;
                        return studentBatches.includes(hw.batchId);
                    });
                    const assignedLevelIds = new Set(assignedHWs.map(h => h.levelId));
                    totalTargetedMissions += assignedLevelIds.size;

                    const studentSubs = allSubmissions.filter(sub => sub.studentEmail === student.email);
                    const completedLevelIds = new Set();
                    studentSubs.forEach(sub => {
                        if (sub.status === 'completed') {
                            if (sub.levelId) {
                                completedLevelIds.add(sub.levelId);
                            } else {
                                const matchingHw = allHomeworks.find(h => h.id === sub.homeworkId);
                                if (matchingHw?.levelId) completedLevelIds.add(matchingHw.levelId);
                            }
                        }
                    });
                    const completedMissions = student.completedMissions || [];
                    completedMissions.forEach(id => {
                        const safeId = String(id || '');
                        if (safeId.startsWith('TACTICS_') || safeId.startsWith('KIDS_')) {
                            completedLevelIds.add(id);
                        } else {
                            const matchingHw = allHomeworks.find(h => h.id === id);
                            if (matchingHw?.levelId) completedLevelIds.add(matchingHw.levelId);
                        }
                    });

                    const assignedCompletedCount = [...assignedLevelIds].filter(lvlId => completedLevelIds.has(lvlId)).length;
                    totalSubs += assignedCompletedCount;
                });

                const totalMissions = allHomeworks.length;
                const overallCompletionRate = totalTargetedMissions > 0 
                    ? Math.round((totalSubs / totalTargetedMissions) * 100) 
                    : 0;

                const bootcampHWCount = allHomeworks.filter(hw => String(hw.levelId || '').startsWith('KIDS_')).length;
                const tacticsHWCount = allHomeworks.filter(hw => String(hw.levelId || '').startsWith('TACTICS_')).length;

                // Sort submissions by date descending for timeline
                const recentSubmissions = [...allSubmissions]
                    .sort((a,b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                    .slice(0, 8);

                // Student reports mapping based on unique level completions
                const studentReports = studentsList.map(student => {
                    const studentBatches = allBatches.filter(b => (b.students || []).includes(student.email)).map(b => b.id);
                    
                    const assignedHWs = allHomeworks.filter(hw => {
                        if (hw.assignedTo && Array.isArray(hw.assignedTo)) {
                            return hw.assignedTo.some(email => email.toLowerCase() === student.email.toLowerCase());
                        }
                        if (hw.assignTarget === 'STUDENT') return hw.studentEmail === student.email;
                        return studentBatches.includes(hw.batchId);
                    });

                    const assignedLevelIds = new Set(assignedHWs.map(h => h.levelId));

                    const studentSubs = allSubmissions.filter(sub => sub.studentEmail === student.email);

                    // Find completed levelIds
                    const completedLevelIds = new Set();
                    studentSubs.forEach(sub => {
                        if (sub.status === 'completed') {
                            if (sub.levelId) {
                                completedLevelIds.add(sub.levelId);
                            } else {
                                const matchingHw = allHomeworks.find(h => h.id === sub.homeworkId);
                                if (matchingHw?.levelId) completedLevelIds.add(matchingHw.levelId);
                            }
                        }
                    });
                    const completedMissions = student.completedMissions || [];
                    completedMissions.forEach(id => {
                        const safeId = String(id || '');
                        if (safeId.startsWith('TACTICS_') || safeId.startsWith('KIDS_')) {
                            completedLevelIds.add(id);
                        } else {
                            const matchingHw = allHomeworks.find(h => h.id === id);
                            if (matchingHw?.levelId) completedLevelIds.add(matchingHw.levelId);
                        }
                    });

                    // Find in-progress levelIds
                    const inProgressLevelIds = new Set();
                    studentSubs.forEach(sub => {
                        if (sub.status === 'in_progress') {
                            let lvlId = sub.levelId;
                            if (!lvlId) {
                                const matchingHw = allHomeworks.find(h => h.id === sub.homeworkId);
                                lvlId = matchingHw?.levelId;
                            }
                            if (lvlId && !completedLevelIds.has(lvlId)) {
                                inProgressLevelIds.add(lvlId);
                            }
                        }
                    });

                    const assignedCompletedCount = [...assignedLevelIds].filter(lvlId => completedLevelIds.has(lvlId)).length;
                    const assignedInProgressCount = [...assignedLevelIds].filter(lvlId => inProgressLevelIds.has(lvlId)).length;

                    const rate = assignedLevelIds.size > 0 
                        ? Math.round((assignedCompletedCount / assignedLevelIds.size) * 100) 
                        : 0;
                    
                    return {
                        ...student,
                        assignedCount: assignedLevelIds.size,
                        completedCount: assignedCompletedCount,
                        inProgressCount: assignedInProgressCount,
                        completionRate: rate,
                        lastSubmission: studentSubs.sort((a,b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0] || null
                    };
                }).sort((a, b) => {
                    if (erpSort === 'COMPLETION_DESC') return b.completionRate - a.completionRate;
                    if (erpSort === 'COMPLETION_ASC') return a.completionRate - b.completionRate;
                    if (erpSort === 'NAME_AZ') {
                        const nameA = a.name || a.email.split('@')[0];
                        const nameB = b.name || b.email.split('@')[0];
                        return nameA.localeCompare(nameB);
                    }
                    if (erpSort === 'IN_PROGRESS_DESC') return b.inProgressCount - a.inProgressCount;
                    if (erpSort === 'ASSIGNED_DESC') return b.assignedCount - a.assignedCount;
                    return 0;
                });

                return (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        {/* Summary Stats Grid */}
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                            <div className="glass-panel" style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800' }}>OVERALL COMPLETION</div>
                                <div style={{fontSize: '32px', fontWeight: '800', color: '#059669' }}>{overallCompletionRate}%</div>
                                <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>{totalSubs} completed of {totalTargetedMissions} targeted</div>
                            </div>
                            <div className="glass-panel" style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800' }}>DEPLOYED MISSIONS</div>
                                <div style={{fontSize: '32px', fontWeight: '800', color: '#2563eb' }}>{totalMissions}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>{bootcampHWCount} Bootcamp / {tacticsHWCount} Tactics</div>
                            </div>
                            <div className="glass-panel" style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800' }}>TOTAL STUDENTS</div>
                                <div style={{fontSize: '32px', fontWeight: '800', color: '#f59e0b' }}>{studentsList.length}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Enrolled in {allBatches.length} active batches</div>
                            </div>
                        </div>

                        {/* Roster & Timeline Grid */}
                        <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '30px' }}>
                            
                            {/* Student Performance Roster */}
                            <div className="glass-panel" style={{padding: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                                    <h3 style={{margin: 0, fontSize: '20px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800' }}>
                                        🎓 Student Progress Ledger
                                    </h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-secondary)' }}>Sort Ledger:</span>
                                        <select value={erpSort} onChange={e => setErpSort(e.target.value)} style={{ padding: '10px 14px', borderRadius: '10px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', boxShadow: 'var(--neu-shadow)', outline: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                                            <option value="COMPLETION_DESC">📈 Completion: Highest First</option>
                                            <option value="COMPLETION_ASC">📉 Completion: Lowest First</option>
                                            <option value="NAME_AZ">🔤 Student Name: A-Z</option>
                                            <option value="IN_PROGRESS_DESC">⏳ Active Sessions: Most First</option>
                                            <option value="ASSIGNED_DESC">📝 Assigned Count: Most First</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{overflowX: 'auto' }}>
                                    <table style={{width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{borderBottom: '1px solid rgba(163, 177, 198, 0.3)' }}>
                                                <th style={{textAlign: 'left', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>Student</th>
                                                <th style={{textAlign: 'center', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>Assigned</th>
                                                <th style={{textAlign: 'center', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>Completed</th>
                                                <th style={{textAlign: 'center', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>In Progress</th>
                                                <th style={{textAlign: 'left', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>Completion Rate</th>
                                                <th style={{textAlign: 'left', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>Last Active</th>
                                                <th style={{textAlign: 'right', padding: '12px 10px', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '800' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {studentReports.map(student => {
                                                const formattedRate = student.completionRate;
                                                let progressColor = '#ef4444';
                                                if (formattedRate > 70) progressColor = '#059669';
                                                else if (formattedRate > 40) progressColor = '#f59e0b';

                                                return (
                                                    <tr key={student.id} style={{borderBottom: '1px solid rgba(163, 177, 198, 0.2)', verticalAlign: 'middle' }}>
                                                        <td style={{padding: '14px 10px' }}>
                                                            <div style={{fontWeight: '800', color: 'var(--text-primary)' }}>{student.name || student.email.split('@')[0]}</div>
                                                            <div style={{fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>@{student.username || student.email.split('@')[0]}</div>
                                                        </td>
                                                        <td style={{padding: '14px 10px', textAlign: 'center', fontWeight: '800', color: 'var(--text-primary)' }}>{student.assignedCount}</td>
                                                        <td style={{padding: '14px 10px', textAlign: 'center', fontWeight: '800', color: 'var(--text-primary)' }}>{student.completedCount}</td>
                                                        <td style={{padding: '14px 10px', textAlign: 'center', fontWeight: '800', color: student.inProgressCount > 0 ? '#d97706' : 'var(--text-primary)' }}>
                                                            {student.inProgressCount > 0 ? `⏳ ${student.inProgressCount}` : '0'}
                                                        </td>
                                                        <td style={{padding: '14px 10px' }}>
                                                            <div style={{display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <div style={{width: '60px', height: '8px', background: 'var(--bg-primary)', borderRadius: '4px', overflow: 'hidden', boxShadow: 'var(--neu-shadow-inset)' }}>
                                                                    <div style={{ width: `${formattedRate}%`, height: '100%', background: progressColor }}></div>
                                                                </div>
                                                                <span style={{fontWeight: '800', color: progressColor, fontSize: '13px' }}>{formattedRate}%</span>
                                                            </div>
                                                        </td>
                                                        <td style={{padding: '14px 10px', fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                                            {student.lastSubmission ? new Date(student.lastSubmission.submittedAt).toLocaleDateString() : 'Never'}
                                                        </td>
                                                        <td style={{padding: '14px 10px', textAlign: 'right' }}>
                                                            {student.lastSubmission ? (
                                                                <button 
                                                                    onClick={() => setReviewSubmission(student.lastSubmission)}
                                                                    className="neu-btn"
                                                                    style={{padding: '6px 12px', fontSize: '12px', background: 'var(--bg-primary)', color: '#8b5cf6', border: 'none', boxShadow: 'var(--neu-shadow)' }}
                                                                    onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'}
                                                                    onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}
                                                                >
                                                                    🔍 Review Last
                                                                </button>
                                                            ) : (
                                                                <span style={{fontSize: '11px', color: 'var(--text-secondary)', opacity: 0.6, fontWeight: '600' }}>No submissions</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Recent Submissions Feed */}
                            <div className="glass-panel" style={{padding: '25px', border: 'none', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                                <h3 style={{margin: '0 0 20px 0', fontSize: '20px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800' }}>
                                    ⏱️ Live Submission Timeline
                                </h3>
                                {recentSubmissions.length === 0 ? (
                                    <div style={{color: 'var(--text-secondary)', textAlign: 'center', padding: '30px', fontWeight: '600' }}>No student homework submissions found yet.</div>
                                ) : (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {recentSubmissions.map(sub => (
                                            <div key={sub.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '15px 20px', borderRadius: '12px' }}>
                                                <div>
                                                    <div style={{fontWeight: '800', color: 'var(--text-primary)', fontSize: '15px' }}>
                                                        {sub.studentName} <span style={{fontWeight: '600', color: 'var(--text-secondary)', fontSize: '13px' }}>submitted</span> {sub.homeworkTitle}
                                                    </div>
                                                    <div style={{color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>
                                                        📅 {new Date(sub.submittedAt).toLocaleString()} | Type: {sub.gameType || 'Standard Chess'}
                                                    </div>
                                                </div>
                                                <button 
                                                    onClick={() => setReviewSubmission(sub)}
                                                    className="neu-btn"
                                                    style={{padding: '8px 16px', fontSize: '13px', background: 'var(--bg-primary)', color: '#059669', border: 'none', boxShadow: 'var(--neu-shadow)', fontWeight: 'bold' }}
                                                    onMouseOver={e=>e.currentTarget.style.boxShadow='var(--neu-shadow-hover)'}
                                                    onMouseOut={e=>e.currentTarget.style.boxShadow='var(--neu-shadow)'}
                                                >
                                                    🔍 Deep Review
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                );
            })()}
            </div>
        </div>
    );
}
